import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { SelectorOption } from 'components/AdvancedDesignationsSelector';
import { useAlerts } from 'hooks';
import { useAppContext } from 'hooks/useAppContext';
import { useCreateCampaignMutation } from 'queries/UseCampaigns';
import { CampaignDesignation } from '../../../services';
import { CampaignSchema, ICampaignSchema } from './Campaign.Schema';
import { CampaignEditForm } from './CampaignEditForm';

export const CampaignCreate = (): JSX.Element => {
  const navigate = useNavigate();
  const [pushAlert] = useAlerts();

  const {
    setSelectedCampaignName,
    setSelectedCampaignId,
    setShowNewCampaignDialog,
    selectedOrganization: { id: organizationId }
  } = useAppContext();

  const { mutateAsync: createCampaignMutation, isLoading: isCampaignSaving } =
    useCreateCampaignMutation(organizationId);

  const methods = useForm<ICampaignSchema>({
    resolver: yupResolver(CampaignSchema),
    mode: 'onBlur'
  });

  const breadcrumbs = [
    {
      label: 'All Campaigns',
      path: '/g/fundraising/campaigns'
    },
    {
      label: 'New Campaign',
      path: ''
    }
  ];

  const handleCampaignCreate = async (data: FieldValues) => {
    const campaignDesignations: CampaignDesignation[] = data.designations.map(
      (d: SelectorOption): CampaignDesignation => ({
        id: d.value as string,
        title: d.label,
        type: d.type
      })
    );

    try {
      const body = data as ICampaignSchema;

      const response = await createCampaignMutation({
        ...body,
        goalAmount: data?.goalAmount ? Number(data?.goalAmount) : undefined,
        organizationId,
        campaignDesignations
      });

      setSelectedCampaignName(response.title);
      setSelectedCampaignId(response.id);
      setShowNewCampaignDialog(true);
      navigate(`/g/fundraising/campaigns/${response.id}/`, {
        state: { showNewCampaignModal: true }
      });
    } catch (e) {
      pushAlert({
        title: 'Error creating campaign',
        description: `${e as string}`,
        severity: 'error',
        autoHideDuration: 10000
      });
    }
  };

  return (
    <CampaignEditForm
      formTitle="New Campaign"
      methods={methods}
      breadcrumbs={breadcrumbs}
      buttonLabel="Create Campaign"
      isLoading={isCampaignSaving}
      onSubmit={handleCampaignCreate}
    />
  );
};
